import {useLocation, useNavigate} from "react-router-dom";
import {useLegacyLoginMutation} from "../../store/auth/auth-store";
import {AuthRequest, getLegacyAuthHeader} from "@ots-frontend/ots-data";
import {useAppThunkDispatch} from "../../store/hooks/useAppThunkDispatch";
import {useCallback} from "react";
import {useAuth0} from "@auth0/auth0-react";
import {setAuthToken} from "../../store/auth/actions";
import {AppLoginPage, useAuth} from "@ots-frontend/ots-ui";
import DispatchGoLogo from "../../../assets/dispatchgo.svg?react";
import {Button as CatalystButton} from "catalyst";
import OtsLogo from "../../../assets/ots-logo.svg?react";

export function LoginPage() {
  const auth = useAuth();

  const dispatch = useAppThunkDispatch();
  const navigate = useNavigate();
  const location = useLocation()

  // legacy login
  const [loginLegacy, {isLoading: isLegacyLoading, error: legacyError}] = useLegacyLoginMutation();

  // auth0 login
  const {loginWithRedirect, isAuthenticated} = useAuth0();

  const params = new URLSearchParams(location.search);
  const returnTo = params.get("returnTo") || "/dispatch";

  const onLegacyFormSubmit = useCallback(async (values: { username: string | undefined; password: string | undefined }) => {
    const request: AuthRequest = {username: values.username, password: values.password};
    const result = await loginLegacy(request).unwrap();
    dispatch(setAuthToken(getLegacyAuthHeader(result)));
    navigate(decodeURIComponent(returnTo), {replace: true});
  }, [dispatch, loginLegacy, navigate, returnTo])

  const onAuth0Login = useCallback(() => {
    const finalReturnTo = returnTo || "/dispatch"
    loginWithRedirect({
      appState: {returnTo: finalReturnTo},
      authorizationParams: {
        prompt: "login",
      }
    });
  }, [loginWithRedirect, returnTo])

  if (auth && auth.authenticated) {
    navigate("/dispatch", {replace: true});
  }

  return (
    <AppLoginPage
      brandLogo={
        <DispatchGoLogo
          aria-label="DispatchGo"
          className="h-12 w-auto max-w-[260px]"
        />
      }
      poweredBy={{
        logo: <OtsLogo className="h-3.5 w-3.5"/>,
        name: "Oilfield Tracking Services",
        url: "https://oilfieldtrackingservices.com",
      }}
      usernameLabel="Username"
      usernameDescription="Use the username associated with your account."
      usernamePlaceholder="username"
      signInButtonLabel="Sign in with username"
      externalError={legacyError}
      isLegacyLoading={isLegacyLoading}
      isSsoAuthenticated={isAuthenticated}
      onLegacySubmit={onLegacyFormSubmit}
      onSsoLogin={onAuth0Login}
      marketingEyebrow="DispatchGo Dashboard"
      marketingHeadline={(
        <>
          Manage your fleet in real time, from{" "}
          <span className="underline decoration-[#E1251B]/60 decoration-2 underline-offset-4">
            dispatch to delivery
          </span>
          .
        </>
      )}
      marketingDescription={(
        <>
          DispatchGo helps oilfield teams create electronic dispatches,
          coordinate drivers, and track jobs as they move through the field.
          Sign in to manage loads, shifts, tickets, and site activity across
          your operation.
        </>
      )}
      marketingFeatures={[
        {
          title: "Electronic dispatches for daily work",
          description: "Build and assign dispatch jobs for drivers, then keep the office and field aligned as work is accepted, started, and completed.",
        },
        {
          title: "Near real-time job tracking",
          description: "Monitor drivers, loads, tickets, and operating status from a dispatch workspace built for oil, gas, and water hauling.",
        },
        {
          title: "Connected with FieldGo, WaterGo, and OilGo",
          description: "DispatchGo fits into the OTS field app ecosystem so dispatch, field data, water logistics, and oil tracking stay connected.",
        },
      ]}
      marketingFooter={(
        <div className="mt-8 space-y-2 text-[0.7rem] text-slate-500">
          <p>Looking for the mobile app?</p>
          <div className="flex flex-wrap gap-2">
            <CatalystButton
              outline
              size="sm"
              href="https://apps.apple.com/us/app/dispatchgo/id1600523034"
              target="_blank"
              rel="noreferrer">
              Download on the App Store
            </CatalystButton>
            <CatalystButton
              outline
              size="sm"
              href="https://play.google.com/store/apps/details?id=com.wm.dispatchgo"
              target="_blank"
              rel="noreferrer">
              Get it on Google Play
            </CatalystButton>
          </div>
          <p>
            Need access to another yard or tenant? Contact your DispatchGo
            administrator.
          </p>
        </div>
      )}
    />
  )
}


